import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { Typography } from "@insite/mobius/index";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";

const enum fields {
    faqAccordianMainTitle = "faqAccordianMainTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.faqAccordianMainTitle]: string;
    };
}
const mapStateToProps = (state: CustomApplicationState) => {
    const faqData = (state?.t_components?.faq?.faqs as any)?.faqs || [];

    return {
        faqData,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CENFaqWidgetStyles {
    accordianSection?: AccordionSectionPresentationProps;
    brandAccordainMainTitleStyles?: InjectableCss;
    mainContainer?: InjectableCss;
    mobileAccordionWrapper?: InjectableCss;
    desktopListWrapper?: InjectableCss;
    desktopFaqItem?: InjectableCss;
    desktopFaqQuestion?: InjectableCss;
    desktopFaqAnswer?: InjectableCss;
}

export const cenFaqWidgetStyles: CENFaqWidgetStyles = {
    mainContainer: {
        css: css`
            width: 100%;
            margin: 96px 20px 64px;
            max-width: 1800px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 56px 0 45px;
                    `,
                    css`
                        margin: 56px 0 45px;
                    `,
                    css`
                        margin: 56px 0 45px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    accordianSection: {
        toggleIconProps: {
            src: Minus,
        },
        expandIconProps: {
            src: Plus,
        },
        panelProps: {
            css: css`
                border-bottom: 1px solid #e0dfe0;
                border-left: none;
                border-right: none;
            `,
        },
        headerProps: {
            css: css`
                button[aria-expanded="true"] {
                    > span {
                        padding: 24px 0px;
                    }
                }
                button[aria-expanded="false"] {
                    > span {
                        padding: 12px 0px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    max-width: 85%;
                                `,
                                null,
                                null,
                                null,
                                null,
                            ])}
                    }
                }
                button {
                    border-top: 1px solid #e0dfe0;
                    border-left: none;
                    border-right: none;
                    border-bottom: 1px solid #e0dfe0;
                    background-color: #ffffff;

                    > span {
                        font-size: ${TYPOGRAPHY.size.largest};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: 32px;
                        color: ${COLORS.text.mineShaft};
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    font-size: ${TYPOGRAPHY.size.large};
                                `,
                                css`
                                    font-size: ${TYPOGRAPHY.size.large};
                                `,
                                null,
                                null,
                                null,
                            ])}
                        svg {
                            height: 19.5px;
                            width: 19.5px;
                        }
                    }
                    &:focus {
                        border-top: 1px solid #e0dfe0;
                        border-bottom: 1px solid #e0dfe0;
                        border-left: none;
                        border-right: none;
                    }
                    &[aria-expanded="true"] {
                        border-bottom: none;
                    }
                }
            `,
        },
    },
    brandAccordainMainTitleStyles: {
        css: css`
            margin-bottom: 8px;
            font-size: ${TYPOGRAPHY.size.xxlarge};
            color: ${COLORS.text.mineShaft};
            font-weight: 700;
            line-height: 40px;
            @media (max-width:768px){
             padding-left:15px;
            }
           
        `,
    },
    mobileAccordionWrapper: {
        css: css`
            display: none;
            @media (max-width: 767px) {
                display: block;
            }
        `,
    },
    desktopListWrapper: {
        css: css`
            display: none;
            max-width:917px;
            @media (min-width: 768px) {
                display: block;
            }
        `,
    },

    desktopFaqItem: {
        css: css`
            padding: 16px 0px;
            border-bottom: 1px solid #e0dfe0;

            :last-child {
                border-bottom: none;
            }
        `,
    },
    desktopFaqQuestion: {
        css: css`
            font-size: ${TYPOGRAPHY.size.largest};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 32px;
            color: ${COLORS.text.mineShaft};
            margin-bottom: 4px;
        `,
    },
    desktopFaqAnswer: {
        css: css`
            white-space: pre-line;
            span {
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 24px;
                color: ${COLORS.text.mineShaft};
            }
        `,
    },
};

export const CENFaqWidget: FC<Props> = ({ fields, faqData }) => {
    const [expandedState, setExpandedState] = useState<boolean[]>([]);

    const toggleAccordion = (index: number) => {
        setExpandedState(
            prev => faqData.map((_: unknown, i: number) => (i === index ? !prev[i] : false)),
        );
    };

    if (faqData.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...cenFaqWidgetStyles.mainContainer} data-test-selector="brandFaqTitle">
            <StyledWrapper {...cenFaqWidgetStyles.brandAccordainMainTitleStyles}>
                {fields.faqAccordianMainTitle}
            </StyledWrapper>

            <StyledWrapper {...cenFaqWidgetStyles.desktopListWrapper}>
                {faqData.map((item: any, index: any) => (
                    <StyledWrapper key={index} {...cenFaqWidgetStyles.desktopFaqItem}>
                        <StyledWrapper {...cenFaqWidgetStyles.desktopFaqQuestion}>{item.question}</StyledWrapper>
                        <StyledWrapper {...cenFaqWidgetStyles.desktopFaqAnswer}>
                            <Typography>{parse(item.answer)}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>

            <StyledWrapper {...cenFaqWidgetStyles.mobileAccordionWrapper}>
                <Accordion headingLevel={3}>
                    {faqData.map((item: any, index: any) => {
                        return (
                            <AccordionSection
                                key={index}
                                title={item.question}
                                expanded={expandedState[index]}
                                onTogglePanel={() => toggleAccordion(index)}
                                {...cenFaqWidgetStyles.accordianSection}
                            >
                                <StyledWrapper
                                    css={css`
                                        white-space: pre-line;
                                    `}
                                >
                                    <Typography>{parse(item.answer)}</Typography>
                                </StyledWrapper>
                            </AccordionSection>
                        );
                    })}
                </Accordion>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CENFaqWidget),
    definition: {
        group: "Basic",
        displayName: "CEN FAQ Widget",
        fieldDefinitions: [
            {
                name: fields.faqAccordianMainTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
